import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../utils/apiAuth";

export const getProjects = createAsyncThunk(
  "projects/getProjects",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get("/projects");

      if (!response.data.data || response.data.data.length === 0) {
        return [];
      }

      const projects = response.data.data.map((project) => {
        const startDate = project.startedAt
          ? new Date(project.startedAt)
          : null;
        let timeDisplay = "Başlamadı";

        if (startDate) {
          const diffMs = new Date() - startDate;
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

          if (diffDays >= 1) {
            timeDisplay = `${diffDays} gün`;
          } else {
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMinutes = Math.floor(
              (diffMs % (1000 * 60 * 60)) / (1000 * 60)
            );
            timeDisplay = `${diffHours}sa ${diffMinutes}dk`;
          }
        }

        return {
          id: project._id,
          title: project.title,
          description: project.definition,
          type: project.type === "MOBILE" ? "Mobil" : project.type,
          status:
            project.status === "Ongoing"
              ? "Devam Ediyor"
              : "Not Started"
              ? "Henüz Başlamadı"
              : "Tamamlanmadı",
          memberCount: project.memberCount,
          allPositionCount: project.allPositionCount,
          startedAt: project.startedAt,
          timeDisplay,
          progress: 0,
          positions: [],
          team: [],
        };
      });

      // Görselleri arka planda yükle - ana loading state'ini etkilemesin
      setTimeout(() => {
        const imagePromises = projects.map((project) =>
          dispatch(fetchProjectImage(project.id))
            .unwrap()
            .catch(() => {
              return { relatedId: project.id, content: null };
            })
        );
        
        Promise.all(imagePromises).then((imageResults) => {
          imageResults.forEach(({ relatedId, content }) => {
            dispatch({
              type: "projects/fetchProjectImage/fulfilled",
              payload: { relatedId, content },
            });
          });
        });
      }, 0);

      return projects;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Proje listesi yüklenirken bir hata oluştu"
      );
    }
  }
);

// Proje görselini çeken async thunk
export const fetchProjectImage = createAsyncThunk(
  "projects/fetchProjectImage",
  async (relatedId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/images/${relatedId}`, {
        headers: {
          accept: "application/json",
        },
      });

      if (response.data.success) {
        const imageData = response.data.data[0] || null;
        const content = imageData?.content || null;
        const cleanedContent = content
          ? content.replace(/^data:image\/[a-z]+;base64,/, "")
          : null;
        return { relatedId, content: cleanedContent };
      } else {
        return rejectWithValue("Görsel bilgileri alınamadı.");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

// Belirli bir projeyi çek
export const getProjectById = createAsyncThunk(
  "projects/getProjectById",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      const project = response.data.data;
      return {
        id: project._id,
        title: project.title,
        description: project.definition,
        type: project.type,
        status: project.status === "Ongoing" ? "Devam ediyor." : project.status,
        members: project.memberCount || 0,
        remainingTime: "Bilinmiyor",
        progress: project.completed_percentage || 0,
        positions: project.positions || [],
        team: project.teamMembers || [],
        isLive: project.isLive,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Proje detayları yüklenirken bir hata oluştu"
      );
    }
  }
);

// Projenin pozisyonlarını ve takım üyelerini çek
export const getPositionsByProjectId = createAsyncThunk(
  "projects/getPositionsByProjectId",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/positions/getPositionsByProjectId/${projectId}`
      );
      const positionsData = response.data.data;
      // API yanıtını ve teamMembers verisini konsola yazdır

      const teamMembers = positionsData[0]?.projectId?.teamMembers || [];
      return {
        positions: positionsData.map((position) => ({
          _id: position._id,
          positionName: position.positionName?.name || position.positionName,
          expectations: position.expectations || [],
          type: position.type,
          status: position.status,
        })),
        teamMembers: teamMembers.map((member) => ({
          _id: member._id,
          name: member.name,
          email: member.email,
          role: member.role,
          type: member.type,
        })),
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Pozisyonlar ve takım üyeleri yüklenirken bir hata oluştu"
      );
    }
  }
);

const projectSlice = createSlice({
  name: "projects",
  initialState: {
    projectList: [],
    selectedProject: null,
    imageMap: {},
    positions: [],
    teamMembers: [],
    loading: false,
    projectDetailLoading: false,
    error: null,
  },
  reducers: {
    setSelectedProject: (state, action) => {
      state.selectedProject = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Projeleri çekme
      .addCase(getProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projectList = action.payload;
      })
      .addCase(getProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchProjectImage
      .addCase(fetchProjectImage.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchProjectImage.fulfilled, (state, action) => {
        const { relatedId, content } = action.payload;
        state.imageMap[relatedId] = content;
      })
      .addCase(fetchProjectImage.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Proje detayını çekme
      .addCase(getProjectById.pending, (state) => {
        state.projectDetailLoading = true;
      })
      .addCase(getProjectById.fulfilled, (state, action) => {
        state.projectDetailLoading = false;
        state.selectedProject = action.payload;
        state.teamMembers = action.payload.team;
      })
      .addCase(getProjectById.rejected, (state, action) => {
        state.projectDetailLoading = false;
        state.error = action.error.message;
      })
      // Pozisyonları ve takım üyelerini çekme
      .addCase(getPositionsByProjectId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPositionsByProjectId.fulfilled, (state, action) => {
        state.loading = false;
        state.positions = action.payload.positions;
        state.teamMembers = action.payload.teamMembers;
      })
      .addCase(getPositionsByProjectId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedProject } = projectSlice.actions;
export default projectSlice.reducer;
